import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import Api from "../../api";
import { toHumanDate, showNotification } from "../../utils";
import "./scheduler.css";

const WEEKDAYS = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];

const WEEKDAY_FILTER_OPTIONS = WEEKDAYS.map((day) => ({
  label: day,
  value: day,
}));

const LANGUAGE_FILTER_OPTIONS = [
  { label: "EN", value: "EN" },
  { label: "FR", value: "FR" },
  { label: "PT", value: "PT" },
  { label: "DE", value: "DE" },
];

const getDayOfWeek = (dateString) => {
  const date = new Date(dateString);
  return WEEKDAYS[date.getDay()];
};

const formatLocationDisplay = (clase) => {
  const isVirtual =
    clase.location === "VIRTUAL" || clase.location_name?.startsWith("S-");
  if (isVirtual) {
    return `Virtual | ${clase.location_name || "—"}`;
  }
  return clase.location_name || "—";
};

const enrichClassRow = (clase) => ({
  ...clase,
  startDateObj: new Date(clase.start_date),
  endDateObj: new Date(clase.end_date),
  weekday: getDayOfWeek(clase.start_date),
  startDateLabel: [
    getDayOfWeek(clase.start_date),
    toHumanDate(clase.start_date, "DD/MM/YYYY"),
    toHumanDate(clase.start_date, "HH:mm"),
  ].join(" "),
});

const getFormattedCurDate = () => {
  const currentDate = new Date();
  currentDate.setMinutes(currentDate.getMinutes() + 10);
  currentDate.setUTCHours(currentDate.getUTCHours() - 5);

  const year = currentDate.getUTCFullYear();
  const month = String(currentDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(currentDate.getUTCDate()).padStart(2, "0");
  const hours = String(currentDate.getUTCHours()).padStart(2, "0");
  const minutes = String(currentDate.getUTCMinutes()).padStart(2, "0");
  const seconds = String(currentDate.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const applyGlobalFilter = (rows, filters, globalFilterFields) => {
  if (!Array.isArray(rows)) return [];
  const globalValue = filters.global?.value;
  if (!globalValue?.trim()) return rows;

  const search = globalValue.trim().toLowerCase();
  return rows.filter((row) =>
    globalFilterFields.some((field) => {
      const value = row[field];
      if (value == null) return false;
      return String(value).toLowerCase().includes(search);
    })
  );
};

const applyColumnFilters = (rows, filters) => {
  if (!Array.isArray(rows)) return [];

  return rows.filter((row) => {
    if (filters.id?.value) {
      const search = String(filters.id.value).toLowerCase();
      if (!String(row.id ?? "").toLowerCase().includes(search)) return false;
    }

    if (filters.startDateLabel?.value) {
      const search = String(filters.startDateLabel.value).toLowerCase();
      if (!String(row.startDateLabel ?? "").toLowerCase().includes(search)) {
        return false;
      }
    }

    if (filters.weekday?.value && row.weekday !== filters.weekday.value) {
      return false;
    }

    if (filters.language?.value && row.language !== filters.language.value) {
      return false;
    }

    if (filters.location_name?.value) {
      const search = String(filters.location_name.value).toLowerCase();
      const locationText = formatLocationDisplay(row).toLowerCase();
      if (
        !locationText.includes(search) &&
        !String(row.location_name ?? "").toLowerCase().includes(search)
      ) {
        return false;
      }
    }

    if (filters.sub_type?.value) {
      const search = String(filters.sub_type.value).toLowerCase();
      if (!String(row.sub_type ?? "").toLowerCase().includes(search)) {
        return false;
      }
    }

    return true;
  });
};

const createInitialFilters = () => ({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  id: { value: null, matchMode: FilterMatchMode.CONTAINS },
  startDateLabel: { value: null, matchMode: FilterMatchMode.CONTAINS },
  weekday: { value: null, matchMode: FilterMatchMode.EQUALS },
  language: { value: null, matchMode: FilterMatchMode.EQUALS },
  location_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  sub_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const dateTimeBodyTemplate = (field) => (rowData) => {
  const value = rowData[field];
  const day = field === "start_date" ? rowData.weekday : getDayOfWeek(value);
  return (
    <div className="scheduler-datetime-cell">
      <span className="dia-semana">{day}</span>
      <span>{toHumanDate(value, "DD/MM/YYYY")}</span>
      <span>{toHumanDate(value, "HH:mm")}</span>
    </div>
  );
};

const locationBodyTemplate = (rowData) => formatLocationDisplay(rowData);

const ClaseCard = ({ clase, isSelected, onSelect, eventType }) => (
  <div
    className={`clase-card ${isSelected ? "clase-card-selected" : ""}`}
    onClick={() => onSelect(clase)}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect(clase);
      }
    }}
    role="button"
    tabIndex={0}
  >
    <h3 className="clase-title">ID: {clase.id}</h3>
    <p>
      <strong>Fecha Inicio:</strong>
    </p>
    <p className="dia-semana">{clase.weekday}</p>
    <p>{toHumanDate(clase.start_date, "DD/MM/YYYY HH:mm")}</p>
    <p>
      <strong>Fecha Finalización:</strong>
    </p>
    <p className="dia-semana">{getDayOfWeek(clase.end_date)}</p>
    <p>{toHumanDate(clase.end_date, "DD/MM/YYYY HH:mm")}</p>
    <p>
      <strong>Ubicación:</strong> {formatLocationDisplay(clase)}
    </p>
    {eventType === "WORKSHOP" && (
      <p>
        <strong>Enfoque:</strong> {clase.sub_type || "—"}
      </p>
    )}
    <p>
      <strong>Lenguaje:</strong> {clase.language}
    </p>
  </div>
);

const SelectionPanel = ({ selectedClass, eventType, onAgendar, registering }) => (
  <aside className="scheduler-selection-panel">
    <h3 className="scheduler-selection-title">Clase preseleccionada</h3>
    {selectedClass ? (
      <div className="scheduler-selection-summary">
        <p>
          <span>ID</span>
          <strong>{selectedClass.id}</strong>
        </p>
        <p>
          <span>Inicio</span>
          <strong>
            {selectedClass.weekday},{" "}
            {toHumanDate(selectedClass.start_date, "DD/MM/YYYY HH:mm")}
          </strong>
        </p>
        <p>
          <span>Fin</span>
          <strong>
            {toHumanDate(selectedClass.end_date, "DD/MM/YYYY HH:mm")}
          </strong>
        </p>
        <p>
          <span>Ubicación</span>
          <strong>{formatLocationDisplay(selectedClass)}</strong>
        </p>
        {eventType === "WORKSHOP" && (
          <p>
            <span>Enfoque</span>
            <strong>{selectedClass.sub_type || "—"}</strong>
          </p>
        )}
        <p>
          <span>Lenguaje</span>
          <strong>{selectedClass.language}</strong>
        </p>
      </div>
    ) : (
      <p className="scheduler-selection-empty">
        Haz clic en una sesión para preseleccionarla.
      </p>
    )}
    <button
      type="button"
      className="scheduler-agendar-btn"
      onClick={onAgendar}
      disabled={!selectedClass || registering}
    >
      {registering ? "Agendando…" : "Agendar clases"}
    </button>
  </aside>
);

function Scheduler(props) {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [filters, setFilters] = useState(createInitialFilters);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await Api.Class.GetByFilters({
        start_date: getFormattedCurDate(),
        type: props.type,
      });

      if (!Array.isArray(data)) {
        showNotification("Error", "Respuesta inválida del servidor", "error");
        setClasses([]);
        return;
      }

      const enriched = data
        .map(enrichClassRow)
        .sort((a, b) => a.startDateObj - b.startDateObj);

      setClasses(enriched);
      setSelectedClass(null);
    } catch (error) {
      showNotification("Error", error.message, "warning");
      setClasses([]);
    } finally {
      setLoading(false);
    }
  }, [props.type]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onGlobalFilterChange = (event) => {
    const value = event.target.value;
    const nextFilters = { ...filters };
    nextFilters.global.value = value;
    setFilters(nextFilters);
    setGlobalFilterValue(value);
  };

  const clearFilters = () => {
    setFilters(createInitialFilters());
    setGlobalFilterValue("");
  };

  const updateFilter = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: {
        ...current[field],
        value: value || null,
      },
    }));
  };

  const handleRowSelect = (clase) => {
    setSelectedClass((current) =>
      current?.id === clase.id ? null : clase
    );
  };

  const handleRowClick = (event) => {
    handleRowSelect(event.data);
  };

  const rowClassName = (rowData) =>
    selectedClass?.id === rowData.id ? "scheduler-row-selected" : "";

  const handleAgendar = async () => {
    if (!selectedClass?.id) return;

    setRegistering(true);
    try {
      await Api.Class.Register(selectedClass.id);
      showNotification("Info", "Clase registrada", "success");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      showNotification("Error", error.message, "warning");
    } finally {
      setRegistering(false);
    }
  };

  const filterClearTemplate = ({ filterClearCallback }) => (
    <button
      type="button"
      className="scheduler-filter-clear-btn"
      onClick={filterClearCallback}
    >
      Limpiar
    </button>
  );

  const filterApplyTemplate = ({ filterApplyCallback }) => (
    <button
      type="button"
      className="scheduler-filter-apply-btn"
      onClick={filterApplyCallback}
    >
      Aplicar
    </button>
  );

  const columnFilterProps = {
    showFilterMatchModes: false,
    filterMenuClassName: "scheduler-column-filter-menu",
    filterClear: filterClearTemplate,
    filterApply: filterApplyTemplate,
  };

  const textFilterTemplate = (options) => (
    <InputText
      value={options.value || ""}
      onChange={(event) => options.filterApplyCallback(event.target.value)}
      placeholder="Buscar…"
      className="scheduler-column-filter-input"
    />
  );

  const startDateFilterTemplate = (options) => (
    <InputText
      value={options.value || ""}
      onChange={(event) => options.filterApplyCallback(event.target.value)}
      placeholder="Ej. Lunes, 20/07…"
      className="scheduler-column-filter-input"
    />
  );

  const handleWeekdayFilterChange = (event) => {
    const nextFilters = { ...filters };
    nextFilters.weekday.value = event.value;
    setFilters(nextFilters);
  };

  const closeMobileFilters = () => setMobileFiltersOpen(false);

  const handleMobileWeekdayChange = (event) => {
    handleWeekdayFilterChange(event);
    closeMobileFilters();
  };

  const handleMobileLanguageChange = (event) => {
    updateFilter("language", event.value);
    closeMobileFilters();
  };

  const handleMobileClearFilters = () => {
    clearFilters();
    closeMobileFilters();
  };

  const tableHeader = (
    <div className="scheduler-table-header">
      <InputText
        value={globalFilterValue}
        onChange={onGlobalFilterChange}
        placeholder="Buscar sesiones"
        className="scheduler-global-filter"
      />
      <Dropdown
        value={filters.weekday.value}
        options={WEEKDAY_FILTER_OPTIONS}
        onChange={handleWeekdayFilterChange}
        placeholder="Día de la semana"
        showClear
        className="scheduler-weekday-filter"
      />
      <button
        type="button"
        className="scheduler-clear-filters-btn"
        onClick={clearFilters}
      >
        Limpiar filtros
      </button>
    </div>
  );
  const languageFilterTemplate = (options) => (
    <Dropdown
      value={options.value}
      options={LANGUAGE_FILTER_OPTIONS}
      onChange={(event) => options.filterApplyCallback(event.value)}
      placeholder="Idioma"
      showClear
      className="scheduler-column-filter-dropdown"
    />
  );

  const globalFilterFields = useMemo(
    () => ["id", "weekday", "language", "location_name", "sub_type"],
    []
  );

  const mobileList = useMemo(() => {
    let rows = applyGlobalFilter(classes, filters, globalFilterFields);
    rows = applyColumnFilters(rows, filters);
    return rows;
  }, [classes, filters, globalFilterFields]);

  const activeMobileFilterCount = useMemo(() => {
    let count = 0;
    if (filters.startDateLabel?.value) count += 1;
    if (filters.weekday?.value) count += 1;
    if (filters.language?.value) count += 1;
    if (filters.location_name?.value) count += 1;
    if (filters.sub_type?.value) count += 1;
    return count;
  }, [filters]);

  const mobileFilterBar = (
    <div className="scheduler-mobile-filters-bar">
      <InputText
        value={globalFilterValue}
        onChange={onGlobalFilterChange}
        placeholder="Buscar sesiones"
        className="scheduler-global-filter"
      />
      <button
        type="button"
        className={`scheduler-mobile-filters-toggle${
          mobileFiltersOpen ? " is-open" : ""
        }`}
        onClick={() => setMobileFiltersOpen((open) => !open)}
        aria-expanded={mobileFiltersOpen}
      >
        Filtros
        {activeMobileFilterCount > 0 && (
          <span className="scheduler-mobile-filters-badge">
            {activeMobileFilterCount}
          </span>
        )}
      </button>
    </div>
  );

  const mobileFilterPanel = (
    <div className="scheduler-mobile-filters-panel">
      <h3 className="scheduler-mobile-filters-panel-title">Filtros</h3>
      <div className="scheduler-mobile-filters-grid">
        <InputText
          value={filters.startDateLabel?.value || ""}
          onChange={(event) =>
            updateFilter("startDateLabel", event.target.value)
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") closeMobileFilters();
          }}
          placeholder="Fecha inicio (ej. Lunes)"
          className="scheduler-mobile-filter-input"
        />
        <Dropdown
          value={filters.weekday.value}
          options={WEEKDAY_FILTER_OPTIONS}
          onChange={handleMobileWeekdayChange}
          placeholder="Día"
          showClear
          className="scheduler-mobile-filter-dropdown"
        />
        <Dropdown
          value={filters.language?.value}
          options={LANGUAGE_FILTER_OPTIONS}
          onChange={handleMobileLanguageChange}
          placeholder="Idioma"
          showClear
          className="scheduler-mobile-filter-dropdown"
        />
        <InputText
          value={filters.location_name?.value || ""}
          onChange={(event) =>
            updateFilter("location_name", event.target.value)
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") closeMobileFilters();
          }}
          placeholder="Ubicación"
          className="scheduler-mobile-filter-input"
        />
        {props.type === "WORKSHOP" && (
          <InputText
            value={filters.sub_type?.value || ""}
            onChange={(event) => updateFilter("sub_type", event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") closeMobileFilters();
            }}
            placeholder="Enfoque"
            className="scheduler-mobile-filter-input"
          />
        )}
      </div>
      <button
        type="button"
        className="scheduler-clear-filters-btn scheduler-mobile-clear-btn"
        onClick={handleMobileClearFilters}
      >
        Limpiar filtros
      </button>
    </div>
  );

  return (
    <div className="scheduler">
      {loading && (
        <>
          <div className="loading-overlay"></div>
          <div className="loading-spinner">
            <ProgressSpinner />
          </div>
        </>
      )}

      <div className="scheduler-container">
        <h2 className="scheduler-section-title">Clases disponibles</h2>

        <section className="available_classes">
          <div className="d_classes">
            {isMobile ? (
              <div className="scheduler-mobile-shell">
                {mobileFilterBar}
                <div className="scheduler-mobile-content">
                  {mobileFiltersOpen ? (
                    mobileFilterPanel
                  ) : (
                    <div className="clase-card-container">
                      {mobileList.length === 0 ? (
                        <div className="clase-card">
                          <p>No hay sesiones disponibles</p>
                        </div>
                      ) : (
                        mobileList.map((clase) => (
                          <ClaseCard
                            key={clase.id}
                            clase={clase}
                            isSelected={selectedClass?.id === clase.id}
                            onSelect={handleRowSelect}
                            eventType={props.type}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <DataTable
                value={classes}
                dataKey="id"
                filters={filters}
                filterDisplay="menu"
                globalFilterFields={globalFilterFields}
                header={tableHeader}
                emptyMessage="No hay sesiones disponibles"
                sortField="startDateObj"
                sortOrder={1}
                removableSort
                scrollable
                scrollHeight="flex"
                onRowClick={handleRowClick}
                rowClassName={rowClassName}
                className="scheduler-datatable"
                tableStyle={{ minWidth: "48rem" }}
              >
                <Column
                  field="id"
                  header="ID Clase"
                  sortable
                  filter
                  filterElement={textFilterTemplate}
                  {...columnFilterProps}
                  style={{ minWidth: "5rem" }}
                />
                <Column
                  field="startDateObj"
                  filterField="startDateLabel"
                  header="Fecha Inicio"
                  sortable
                  dataType="date"
                  filter
                  filterElement={startDateFilterTemplate}
                  {...columnFilterProps}
                  body={dateTimeBodyTemplate("start_date")}
                  style={{ minWidth: "7.5rem" }}
                />
                <Column
                  field="endDateObj"
                  header="Fecha Finalización"
                  sortable
                  dataType="date"
                  body={dateTimeBodyTemplate("end_date")}
                  style={{ minWidth: "7.5rem" }}
                />
                <Column
                  field="location_name"
                  header="Ubicación"
                  sortable
                  filter
                  filterElement={textFilterTemplate}
                  {...columnFilterProps}
                  body={locationBodyTemplate}
                  style={{ minWidth: "9rem" }}
                />
                {props.type === "WORKSHOP" && (
                  <Column
                    field="sub_type"
                    header="Enfoque"
                    sortable
                    filter
                    filterElement={textFilterTemplate}
                    {...columnFilterProps}
                    style={{ minWidth: "7rem" }}
                  />
                )}
                <Column
                  field="language"
                  header="Lenguaje"
                  sortable
                  filter
                  filterElement={languageFilterTemplate}
                  {...columnFilterProps}
                  style={{ minWidth: "5.5rem" }}
                />
              </DataTable>
            )}
          </div>
        </section>

        <SelectionPanel
          selectedClass={selectedClass}
          eventType={props.type}
          onAgendar={handleAgendar}
          registering={registering}
        />
      </div>
    </div>
  );
}

export default Scheduler;
